import React from 'react';
import { useParams } from 'react-router-dom';

import { FeatureFlag, Role } from '@ndvr/auth-api';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RoutingTabs } from '@/components/RoutingTabs';
import { useUserHasRole } from 'views/admin/dev-queries';
import { useFeatureFlag } from 'views/auth/useFeatureFlag';
import { Title } from 'widgets';

import { AccountOnboardingAdmin } from './accountOnboarding/AccountOnboardingAdmin';
import { FeatureFlagList } from './featureFlag/FeatureFlagList';
import { InternalUserProfileDetails } from './internalUserProfiles/InternalUserProfileDetails';
import { InternalUserProfilesList } from './internalUserProfiles/InternalUserProfilesList';
import { Journal } from './journal/Journal';
import { MoveHouseholdsAdmin } from './moveHouseholds/MoveHouseholdsAdmin';
import { OnboardingDebugAdmin } from './onboardingDebug/OnboardingDebugAdmin';
import { PlatformControls } from './platform/PlatformControls';
import { ReportsAdmin } from './ReportsAdmin';
import { TenantTeamAdmin } from './tenantTeam/TenantTeamAdmin';
import { DuplicateUsers } from './users/DuplicateUsers';

export const SystemAdmin = () => (
    <ErrorBoundary>
        <SystemAdminInternal />
    </ErrorBoundary>
);

const SystemAdminInternal = () => {
    const { data: platformControlsEnabled } = useUserHasRole(Role.PLATFORM_CONTROLS_EDITOR);
    const { data: isFeatureFlagManager } = useUserHasRole(Role.FEATURE_FLAG_MANAGER);
    const { data: isSeniorAdvisor } = useUserHasRole(Role.SENIOR_ADVISOR);
    const { data: isSupportUser } = useUserHasRole(Role.SUPPORT_USER);
    const moveHouseholdsAcrossTenantsEnabled = useFeatureFlag(FeatureFlag.MOVE_HOUSEHOLDS_ACROSS_TENANTS);
    const onboardingEnabled = useFeatureFlag(FeatureFlag.ONBOARDING);
    const params = useParams();
    const activeTab = params.tab;
    const tabRemainder = params['*'];
    const internalUserGUID = activeTab === 'internal-users' && tabRemainder ? tabRemainder.split('/')[0] : undefined;

    const tabs = [
        {
            title: 'Reports',
            url: '/reports',
            Component: <ReportsAdmin />,
        },
        ...(isSeniorAdvisor
            ? [
                  {
                      title: 'Account Onboarding',
                      url: '/account-onboarding',
                      Component: <AccountOnboardingAdmin />,
                  },
              ]
            : []),
        ...(moveHouseholdsAcrossTenantsEnabled
            ? [
                  {
                      title: 'Move Households',
                      url: '/move-households',
                      Component: <MoveHouseholdsAdmin />,
                  },
              ]
            : []),
        ...(onboardingEnabled && isSupportUser
            ? [
                  {
                      title: 'Onboarding Debug',
                      url: '/onboarding-debug',
                      Component: <OnboardingDebugAdmin />,
                  },
              ]
            : []),
        {
            title: 'Hub Users',
            url: '/internal-users',
            Component: <InternalUserProfilesList />,
        },
        {
            title: 'Tenant Team',
            url: '/tenant-team',
            Component: <TenantTeamAdmin />,
        },
        ...(isFeatureFlagManager
            ? [
                  {
                      title: 'Feature flags',
                      url: '/flags',
                      Component: <FeatureFlagList />,
                  },
              ]
            : []),
        {
            title: 'Journal',
            url: '/journal',
            Component: <Journal />,
        },
        ...(platformControlsEnabled
            ? [
                  {
                      title: 'Platform controls',
                      url: '/platform',
                      Component: <PlatformControls />,
                  },
              ]
            : []),

        {
            title: 'Duplicate Users',
            url: '/duplicate-users',
            Component: <DuplicateUsers />,
        },
    ];

    return (
        <div className="flex w-full flex-col">
            <div className="flex flex-row px-8 py-4">
                <Title page>System Admin</Title>
            </div>
            <ErrorBoundary>
                <RoutingTabs tabs={tabs} fallback="reports" baseUrl="/admin">
                    {internalUserGUID ? <InternalUserProfileDetails profileGUID={internalUserGUID} /> : undefined}
                </RoutingTabs>
            </ErrorBoundary>
        </div>
    );
};
